/**
 * /matches — admin matches list view (PR-3 Task 20).
 *
 * Split-pane layout:
 *   - Left: filterable, cursor-paginated table (MatchListTable)
 *   - Right (≥1280px): selected match detail (MatchDetail)
 *   - Sticky bottom: "Compare N" bar when 2-10 rows are multi-selected
 *
 * URL contract:
 *   - All filter params round-trip via ?status=&origin=&… so deep
 *     links are shareable.
 *   - Selected match for the right pane is mirrored via `?id=` (push
 *     on click so the back button works).
 *
 * Selection cap of 10 mirrors the backend `/v1/admin/matches/compare`
 * guard (BadRequestError thrown above 10).
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-matches-summaries-design.md §7.1
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useApiUrl } from '@refinedev/core';
import { Plus } from 'lucide-react';
import { adminFetch, AdminApiError } from '@/providers/admin-api';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { MatchFilters } from './components/MatchFilters';
import { MatchHealthBar } from './components/MatchHealthBar';
import { MatchListTable, type MatchListItem } from './components/MatchListTable';
import { nextSort } from '@/components/table/sort';
import { MatchDetail } from './components/MatchDetail';

const MULTI_SELECT_CAP = 10;
const PAGE_SIZE = 50;
// Side panel only renders on wide viewports. Below this we fall back
// to detail-on-navigate via /matches/:matchId.
const SIDE_PANEL_BREAKPOINT_PX = 1280;

export default function MatchesPage() {
  const apiUrl = useApiUrl();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { hasPermission } = useAdminPermissions();

  const [items, setItems] = useState<MatchListItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [openMatchId, setOpenMatchId] = useState<string | null>(
    params.get('id'),
  );
  const [isWide, setIsWide] = useState(
    typeof window !== 'undefined' &&
      window.innerWidth >= SIDE_PANEL_BREAKPOINT_PX,
  );

  // Track viewport width for split-pane toggling.
  useEffect(() => {
    const onResize = () =>
      setIsWide(window.innerWidth >= SIDE_PANEL_BREAKPOINT_PX);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Build a filters snapshot from URL params. `id` is excluded — it
  // controls which detail to open, not which list rows to fetch.
  const filters = useMemo<Record<string, string>>(() => {
    const obj: Record<string, string> = {};
    for (const [k, v] of params.entries()) {
      if (k !== 'id') obj[k] = v;
    }
    return obj;
  }, [params]);

  const buildQuery = useCallback(
    (extra: Record<string, string> = {}) => {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(filters)) {
        if (v) q.set(k, v);
      }
      for (const [k, v] of Object.entries(extra)) {
        if (v) q.set(k, v);
      }
      if (!q.has('limit')) q.set('limit', String(PAGE_SIZE));
      return q.toString();
    },
    [filters],
  );

  // Initial + filter-change fetch.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminFetch(`${apiUrl}/v1/admin/matches?${buildQuery()}`)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        setItems(d.items ?? []);
        setCursor(d.next_cursor ?? null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(
          e instanceof AdminApiError ? e.message : 'Failed to load matches',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, buildQuery]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await adminFetch(
        `${apiUrl}/v1/admin/matches?${buildQuery({ cursor })}`,
      );
      const d = await r.json();
      setItems(prev => [...prev, ...(d.items ?? [])]);
      setCursor(d.next_cursor ?? null);
    } catch (e: unknown) {
      setError(
        e instanceof AdminApiError ? e.message : 'Failed to load more matches',
      );
    } finally {
      setLoadingMore(false);
    }
  }, [apiUrl, buildQuery, cursor, loadingMore]);

  const onFiltersChange = useCallback(
    (next: Record<string, string>) => {
      const merged = new URLSearchParams();
      for (const [k, v] of Object.entries(next)) {
        if (v) merged.set(k, v);
      }
      if (openMatchId) merged.set('id', openMatchId);
      setParams(merged);
    },
    [openMatchId, setParams],
  );

  // Server-side sort via URL params (sort_by/sort_order). Because `filters` is
  // derived from the URL, updating these re-runs the fetch effect, which resets
  // items + cursor — the correct behavior for a re-sorted cursor-paginated list.
  const handleSort = useCallback(
    (field: string) => {
      const cur = {
        field: filters.sort_by ?? null,
        order: (filters.sort_order === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc',
      };
      const next = nextSort(cur, field);
      const merged: Record<string, string> = { ...filters };
      if (next.field) {
        merged.sort_by = next.field;
        merged.sort_order = next.order;
      } else {
        delete merged.sort_by;
        delete merged.sort_order;
      }
      onFiltersChange(merged);
    },
    [filters, onFiltersChange],
  );

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < MULTI_SELECT_CAP) next.add(id);
      return next;
    });
  }, []);

  const onOpenDetail = useCallback(
    (id: string) => {
      if (isWide) {
        setOpenMatchId(id);
        const next = new URLSearchParams(params);
        next.set('id', id);
        setParams(next);
      } else {
        nav(`/matches/${id}`);
      }
    },
    [isWide, nav, params, setParams],
  );

  const goCompare = useCallback(() => {
    if (selectedIds.size < 2) return;
    nav(`/matches/compare?ids=${[...selectedIds].join(',')}`);
  }, [nav, selectedIds]);

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Matches</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {loading ? 'Loading…' : `${items.length} loaded`}
            {cursor ? ' · more available' : ''}
          </p>
        </div>
        {hasPermission('matches.create') && (
          <button
            onClick={() => nav('/matches/new')}
            className={primaryButtonClass}
            type="button"
          >
            <Plus size={16} />
            Create match
          </button>
        )}
      </div>

      <MatchHealthBar />

      <MatchFilters filters={filters} onChange={onFiltersChange} />

      {error && (
        <div className="mt-3 rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'flex-start',
          marginTop: 4,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <MatchListTable
            items={items}
            selectedIds={selectedIds}
            openMatchId={openMatchId}
            multiSelectCap={MULTI_SELECT_CAP}
            onToggleSelect={toggleSelect}
            onOpenDetail={onOpenDetail}
            sortField={filters.sort_by ?? null}
            sortOrder={filters.sort_order === 'asc' ? 'asc' : 'desc'}
            onSort={handleSort}
          />

          {cursor && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className={secondaryButtonClass}
                type="button"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>

        {isWide && (
          <aside
            className="rounded-md border bg-card text-card-foreground"
            style={{
              width: 560,
              flexShrink: 0,
              maxHeight: 'calc(100vh - 160px)',
              overflowY: 'auto',
              position: 'sticky',
              top: 16,
            }}
          >
            {openMatchId ? (
              <MatchDetail key={openMatchId} matchId={openMatchId} />
            ) : (
              <div className="p-6 text-sm text-muted-foreground">
                Select a match to see details.
              </div>
            )}
          </aside>
        )}
      </div>

      {selectedIds.size >= 2 && (
        <div
          className="border-t bg-card shadow-[0_-2px_6px_rgba(0,0,0,0.05)]"
          style={{
            position: 'sticky',
            bottom: 0,
            marginTop: 12,
            marginLeft: -24,
            marginRight: -24,
            padding: '12px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ fontSize: 14 }}>
            {selectedIds.size} selected
            {selectedIds.size === MULTI_SELECT_CAP && (
              <span className="ml-1.5 text-xs text-muted-foreground">
                (max {MULTI_SELECT_CAP})
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setSelectedIds(new Set())}
              className={secondaryButtonClass}
              type="button"
            >
              Clear
            </button>
            {hasPermission('matches.compare') && (
              <button onClick={goCompare} className={primaryButtonClass} type="button">
                Compare {selectedIds.size} matches
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const primaryButtonClass =
  'inline-flex cursor-pointer items-center gap-1.5 rounded-md border-none bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90';

const secondaryButtonClass =
  'cursor-pointer rounded-md border bg-background px-3.5 py-2 text-sm hover:bg-muted';
